import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, Package, Sparkles, X } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { Header } from '@/components/layout/Header';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';

/* ==========================================================================
   S12 — Admin Catalog (browser read-only di catalog_entity)

   Single source of truth: dbgym (master) → sync n8n → OZ-KB Supabase.
   Beauty Checkup proxy-legge da OZ-KB via /api/catalog. Niente CRUD locale.
   ========================================================================== */

interface CatalogItem {
  id: string;
  externalId: string | null;
  source: 'dbgym' | 'woocommerce' | 'knowledge_base' | 'manuale';
  entityType: 'prodotto' | 'servizio';
  slug: string;
  nome: string;
  categoria: string | null;
  sottocategoria: string | null;
  prezzo: number | null;
  durataMinuti: number | null;
  descrizione: string | null;
  attivo: boolean;
  metadata: Record<string, unknown>;
}

type Tab = 'tutti' | 'prodotti' | 'servizi';

const TABS: Array<{ value: Tab; label: string; icon: typeof Package }> = [
  { value: 'tutti', label: 'Tutti', icon: Package },
  { value: 'prodotti', label: 'Prodotti', icon: Package },
  { value: 'servizi', label: 'Servizi', icon: Sparkles },
];

export function AdminCatalog() {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tab, setTab] = useState<Tab>('tutti');
  const [search, setSearch] = useState('');

  // Catalogo mostra solo record vendibili (attivo=true). Per lo storico cliente
  // di prodotti/servizi disattivati useremo un endpoint dedicato sulla scheda
  // cliente — RLS Supabase sull'ANON_KEY nasconde comunque i non vendibili.
  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.get<{ data: CatalogItem[] }>('/catalog?limit=500');
      setItems(res?.data ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Errore di connessione');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let result = items;
    if (tab === 'prodotti') result = result.filter((i) => i.entityType === 'prodotto');
    if (tab === 'servizi') result = result.filter((i) => i.entityType === 'servizio');
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(
        (i) =>
          i.nome.toLowerCase().includes(q) ||
          (i.categoria ?? '').toLowerCase().includes(q) ||
          (i.sottocategoria ?? '').toLowerCase().includes(q),
      );
    }
    return result;
  }, [items, tab, search]);

  const counts = useMemo(
    () => ({
      tutti: items.length,
      prodotti: items.filter((i) => i.entityType === 'prodotto').length,
      servizi: items.filter((i) => i.entityType === 'servizio').length,
    }),
    [items],
  );

  return (
    <AppShell header={<Header />}>
      {/* ---- Title + refresh ---- */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-[family-name:var(--font-heading)] text-2xl font-extrabold text-text-primary tracking-tight">
            Catalogo
          </h1>
          <p className="text-sm text-text-muted mt-0.5">
            Prodotti e servizi sincronizzati da dbgym (sola lettura)
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={isLoading}>
          <RefreshCw className={cn('h-4 w-4 mr-1.5', isLoading && 'animate-spin')} />
          Aggiorna
        </Button>
      </div>

      {/* ---- Search ---- */}
      <div className="mb-3 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per nome o categoria..."
          className="pl-10 pr-10"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary cursor-pointer"
            aria-label="Cancella ricerca"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* ---- Tab bar ---- */}
      <div className="mb-3 flex gap-2 overflow-x-auto scrollbar-hide -mx-5 px-5">
        {TABS.map((t) => {
          const isActive = tab === t.value;
          const n = counts[t.value];
          return (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              className={cn(
                'shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-[var(--radius-full)]',
                'text-sm font-[family-name:var(--font-heading)] font-semibold',
                'transition-all duration-[var(--duration-fast)] cursor-pointer',
                isActive
                  ? 'bg-accent-primary text-white shadow-sm'
                  : 'bg-surface-card border border-border-default text-text-secondary hover:bg-surface-sunken',
              )}
            >
              {t.label}
              <span
                className={cn(
                  'ml-1 px-1.5 py-0.5 rounded-full text-xs tabular-nums',
                  isActive ? 'bg-white/20' : 'bg-surface-sunken text-text-muted',
                )}
              >
                {n}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mb-4" />

      {/* ---- Content ---- */}
      {error && (
        <div className="mb-4 px-4 py-3 rounded-[var(--radius-md)] bg-status-negative-light border border-status-negative/20 text-sm text-status-negative">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} height={68} className="w-full rounded-[var(--radius-md)]" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-sm text-text-muted">
            {search.trim() ? 'Nessun risultato per la ricerca.' : 'Catalogo vuoto.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((item) => (
            <CatalogRow key={item.id} item={item} />
          ))}
        </div>
      )}

      <div className="h-8" />
    </AppShell>
  );
}

function CatalogRow({ item }: { item: CatalogItem }) {
  const Icon = item.entityType === 'servizio' ? Sparkles : Package;
  return (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3 rounded-[var(--radius-md)]',
        'bg-surface-card border border-border-default',
        !item.attivo && 'opacity-60',
      )}
    >
      <div
        className={cn(
          'flex items-center justify-center h-9 w-9 rounded-[var(--radius-sm)] shrink-0',
          item.entityType === 'servizio'
            ? 'bg-accent-secondary-light text-accent-secondary'
            : 'bg-accent-primary-light text-accent-primary',
        )}
      >
        <Icon className="h-4 w-4" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-primary">
            {item.nome}
          </p>
          {!item.attivo && (
            <Badge size="sm" status="neutral">
              Non vendibile
            </Badge>
          )}
          <Badge size="sm" status="info">
            {item.source}
          </Badge>
        </div>
        <p className="text-xs text-text-muted mt-0.5 truncate">
          {item.sottocategoria ?? item.categoria ?? '—'}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="font-[family-name:var(--font-heading)] text-sm font-bold text-text-primary tabular-nums">
          {item.prezzo !== null ? `€${item.prezzo.toFixed(2)}` : '—'}
        </p>
        {item.durataMinuti ? (
          <p className="text-xs text-text-muted tabular-nums">{item.durataMinuti} min</p>
        ) : null}
      </div>
    </div>
  );
}
